import { useState } from 'react';
import { clsx } from '../../../../ds/clsx';
import { Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { errorMessage } from '../../../../lib/api';
import {
  isCalendarConnection,
  isMailConnection,
  providerLabel,
  useConnections,
  usePatchCalendar,
  type CalendarRole,
  type ConnectionCalendar,
  type ConnectionItem,
} from '../../../settings/connections';
import { ActionError, StepFrame, useWizard } from '../StepFrame';
import { Panel } from '../parts';
import styles from './CalendarStep.module.css';

const ROLES: ReadonlyArray<{ value: CalendarRole; label: string; short: string }> = [
  { value: 'ignore', label: 'Nicht nutzen', short: 'Nicht nutzen' },
  { value: 'block', label: 'Nur blockieren', short: 'Blockieren' },
  { value: 'block_and_show', label: 'Blockieren und anzeigen', short: 'Anzeigen' },
];

interface Row {
  connection: ConnectionItem;
  calendar: ConnectionCalendar;
}

function sourceLine(row: Row): string {
  const provider = row.connection.displayName ?? providerLabel(row.connection.provider);
  return `${provider} · ${row.calendar.writable ? 'Schreibrecht vorhanden' : 'nur Leserecht'}`;
}

/** Why a calendar cannot take the bookings (Nutzer 06), or null. */
function bookingBlocker(calendar: ConnectionCalendar): string | null {
  if (!calendar.writable) return 'kein Schreibrecht';
  if (calendar.role === 'block') return 'blockiert nur';
  return null;
}

/** Nutzer 06 / M4: a role per calendar (`PATCH /connections/:id/calendars/:calendarId`) and one booking calendar. */
export function CalendarStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const connections = useConnections();
  const patch = usePatchCalendar();
  const [error, setError] = useState<string | null>(null);

  const items = connections.data?.items ?? [];
  const rows: Row[] = items
    .filter(isCalendarConnection)
    .flatMap((connection) => connection.calendars.map((calendar) => ({ connection, calendar })));
  const mail = items.find((item) => isMailConnection(item) && item.status === 'connected');
  /** A connected account whose calendars could not be read (D-158) explains the empty list. */
  const unreadable = items.find(
    (item) => item.status === 'connected' && item.purposes.includes('calendar') && item.error !== null,
  );

  function change(row: Row, body: { role?: CalendarRole; isBookingCalendar?: boolean }) {
    setError(null);
    patch.mutate(
      { connectionId: row.connection.id, calendarId: row.calendar.id, body },
      { onError: (failure) => setError(errorMessage(failure)) },
    );
  }

  const skip = {
    label: mobile ? 'Später' : 'Später verbinden',
    onClick: () => void wizard.act('skip'),
    loading: wizard.acting === 'skip',
  };
  const primary = {
    label: 'Weiter',
    onClick: () => void wizard.act('continue'),
    loading: wizard.acting === 'continue',
    disabled: rows.length === 0,
  };

  return (
    <StepFrame
      title="Welche Rolle hat jeder Kalender?"
      text="Pro Kalender legen Sie fest, ob er gar nicht genutzt wird, nur Ihre Zeit blockiert oder auch mit Titel in DAMOKLES erscheint. Ein Kalender wird zusätzlich zum Buchungskalender."
      mobileTitle="Rolle je Kalender"
      mobileText="Nicht nutzen, nur blockieren oder auch anzeigen. Ein Kalender nimmt die Buchungen auf."
      width={760}
      gap={22}
      footer={{
        back: wizard.goBack ? { label: 'Zurück', onClick: wizard.goBack } : undefined,
        secondary: skip,
        primary,
      }}
    >
      {mail && !mobile ? (
        <div className={styles.success} role="status">
          <span aria-hidden="true">✓</span>
          {mail.identifier ?? mail.displayName} ist verbunden
        </div>
      ) : null}
      {connections.isPending ? (
        <Skeleton variant="block" height={260} />
      ) : rows.length === 0 ? (
        <Panel className={styles.empty}>
          <span className={styles.emptyTitle}>Noch kein Kalender verbunden.</span>
          <span className={styles.emptyText}>
            {unreadable?.error ??
              'Verbinden Sie im Schritt Postfach Ihr Google-Konto oder richten Sie den Kalender später in den Einstellungen ein.'}
          </span>
        </Panel>
      ) : mobile ? (
        <div className={styles.cards}>
          {rows.map((row) => (
            <MobileCalendarCard key={row.calendar.id} row={row} onChange={(body) => change(row, body)} />
          ))}
        </div>
      ) : (
        <div className={styles.table} role="table" aria-label="Kalender">
          <div className={styles.head} role="row">
            <span role="columnheader">Kalender</span>
            <span role="columnheader">Rolle in DAMOKLES</span>
            <span role="columnheader" className={styles.center}>
              Buchungskalender
            </span>
          </div>
          {rows.map((row) => (
            <CalendarRow key={row.calendar.id} row={row} onChange={(body) => change(row, body)} />
          ))}
        </div>
      )}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      {mobile ? null : (
        <div className={styles.info}>
          In den Buchungskalender schreibt DAMOKLES neue Termine. Von dort gehen Einladung und ICS-Datei an
          die Mandanten, dort landen Zu- und Absagen. Genau ein Kalender, jederzeit wechselbar.
        </div>
      )}
      <ActionError />
    </StepFrame>
  );
}

function RoleSegments({
  calendar,
  onChange,
  mobile,
}: {
  calendar: ConnectionCalendar;
  onChange: (role: CalendarRole) => void;
  mobile: boolean;
}) {
  return (
    <span role="radiogroup" aria-label={`Rolle von ${calendar.name}`} className={styles.segments}>
      {ROLES.map((role) => {
        const disabled = role.value === 'block_and_show' && !calendar.writable;
        const active = calendar.role === role.value;
        return (
          <button
            key={role.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            className={clsx(styles.segment, active && styles.segmentActive)}
            onClick={() => {
              if (!active) onChange(role.value);
            }}
          >
            {mobile ? role.short : role.label}
          </button>
        );
      })}
    </span>
  );
}

function BookingRadio({ row, onSelect }: { row: Row; onSelect: () => void }) {
  const blocker = bookingBlocker(row.calendar);
  const active = row.calendar.isBookingCalendar;
  return (
    <span className={styles.booking}>
      <button
        type="button"
        role="radio"
        aria-checked={active}
        aria-label={`${row.calendar.name} als Buchungskalender`}
        disabled={blocker !== null && !active}
        className={clsx(styles.radio, active && styles.radioActive)}
        onClick={() => {
          if (!active) onSelect();
        }}
      />
      {active ? (
        <span className={styles.bookingActive}>aktiv</span>
      ) : blocker ? (
        <span className={styles.bookingNote}>{blocker}</span>
      ) : null}
    </span>
  );
}

function CalendarRow({
  row,
  onChange,
}: {
  row: Row;
  onChange: (body: { role?: CalendarRole; isBookingCalendar?: boolean }) => void;
}) {
  const dimmed = row.calendar.role === 'ignore';
  return (
    <div
      role="row"
      className={clsx(
        styles.row,
        row.calendar.isBookingCalendar && styles.rowBooking,
        dimmed && styles.rowDimmed,
      )}
    >
      <div role="cell" className={styles.name}>
        <span
          className={clsx(styles.dot, row.calendar.isBookingCalendar && styles.dotBooking)}
          aria-hidden="true"
        />
        <span className={styles.nameText}>
          <span className={styles.calendarName}>{row.calendar.name}</span>
          <span className={styles.calendarSource}>{sourceLine(row)}</span>
        </span>
      </div>
      <div role="cell">
        <RoleSegments calendar={row.calendar} mobile={false} onChange={(role) => onChange({ role })} />
      </div>
      <div role="cell" className={styles.center}>
        <BookingRadio row={row} onSelect={() => onChange({ isBookingCalendar: true })} />
      </div>
    </div>
  );
}

function MobileCalendarCard({
  row,
  onChange,
}: {
  row: Row;
  onChange: (body: { role?: CalendarRole; isBookingCalendar?: boolean }) => void;
}) {
  const blocker = bookingBlocker(row.calendar);
  return (
    <div className={clsx(styles.card, row.calendar.isBookingCalendar && styles.cardBooking)}>
      <div className={styles.cardHead}>
        <span
          className={clsx(styles.dot, row.calendar.isBookingCalendar && styles.dotBooking)}
          aria-hidden="true"
        />
        <span className={styles.calendarName}>{row.calendar.name}</span>
        {row.calendar.writable ? null : <span className={styles.cardNote}>nur Leserecht</span>}
      </div>
      <RoleSegments calendar={row.calendar} mobile onChange={(role) => onChange({ role })} />
      {row.calendar.isBookingCalendar ? (
        <span className={styles.cardBookingRow}>
          <span className={clsx(styles.radio, styles.radioActive)} aria-hidden="true" />
          Buchungskalender · Einladungen und ICS
        </span>
      ) : blocker === null ? (
        <button
          type="button"
          className={styles.cardBookingButton}
          onClick={() => onChange({ isBookingCalendar: true })}
        >
          <span className={styles.radio} aria-hidden="true" />
          Als Buchungskalender nutzen
        </button>
      ) : null}
    </div>
  );
}
